import React, { useState } from 'react';
import axios from 'axios';
import { X, MessageSquare, Send, Shield } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const RecommendationWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState(generateCaptcha());
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    requirements: ''
  });

  function generateCaptcha() {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    return {
      question: `${num1} + ${num2} = ?`,
      answer: num1 + num2
    };
  }

  // Check if user is currently on admin page - only hide on admin routes
  const isOnAdminPage = () => {
    return window.location.pathname.includes('/superadmin');
  };

  // Don't render the widget on admin pages only
  if (isOnAdminPage()) {
    return null;
  }

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      requirements: ''
    });
    setCaptchaValue('');
    setCaptchaQuestion(generateCaptcha());
    setIsSubmitted(false);
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      resetForm();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate captcha
    if (parseInt(captchaValue) !== captchaQuestion.answer) {
      alert('Please solve the math problem correctly');
      return;
    }

    if (!formData.name.trim() || !formData.email.trim() || !formData.requirements.trim()) {
      alert('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/api/recommendations`, formData);
      setIsSubmitted(true);
      setTimeout(() => {
        setIsOpen(false);
        resetForm();
      }, 3000);
    } catch (error) {
      console.error('Error submitting recommendation:', error);
      alert('Error submitting recommendation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Sticky Vertical Tab */}
      <div 
        className={`fixed right-0 top-1/2 transform -translate-y-1/2 z-50 ${isOpen ? 'translate-x-0' : ''}`}
        style={{ 
          writingMode: 'vertical-rl', 
          textOrientation: 'mixed',
          zIndex: 9999 
        }}
      >
        <button
          onClick={handleToggle}
          className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-6 rounded-l-lg shadow-lg transition-all duration-300 font-medium text-sm"
          style={{ 
            writingMode: 'vertical-rl', 
            textOrientation: 'mixed' 
          }}
        >
          <div className="flex items-center space-x-2">
            <MessageSquare size={16} />
            <span>Need Custom Solution?</span>
          </div>
        </button>
      </div>

      {/* Sliding Form Panel */}
      <div className={`fixed right-0 top-0 h-full w-96 bg-white shadow-2xl transform transition-transform duration-300 z-40 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-6">
            <div className="flex justify-between items-center">
              <div className="flex-1 text-center">
                <h3 className="text-xl font-bold">
                  Need Another Solution?
                </h3>
              </div>
              <button
                onClick={handleToggle}
                className="text-gray-200 hover:text-white transition-colors ml-4 flex-shrink-0"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isSubmitted ? (
              /* Success Message */
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Send className="h-8 w-8 text-yellow-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Thank You!
                </h4>
                <p className="text-gray-600 mb-4">
                  Your recommendation has been submitted successfully. Our team will review it and get back to you soon.
                </p>
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    💡 We'll analyze your operational challenges and build a custom AI agent for free!
                  </p>
                </div>
              </div>
            ) : (
              /* Recommendation Form */
              <>
                {/* Marketing Content */}
                <div className="mb-6">
                  <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-lg p-4 mb-4 border border-primary-200">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      🚀 Find Your Perfect Solution
                    </h4>
                    <p className="text-sm text-gray-700 mb-3">
                      We look forward to understanding your requirements and helping you find or build the perfect out-of-the-box solution for your needs.
                    </p>
                    <div className="flex items-center space-x-2 text-sm text-primary-700">
                      <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                      <span className="font-medium">Custom Accelerator Development</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-center">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-lg font-bold text-yellow-600">24hrs</div>
                      <div className="text-xs text-gray-600">Response Time</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-lg font-bold text-yellow-600">Free</div>
                      <div className="text-xs text-gray-600">Custom Solution</div>
                    </div>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="Enter your full name"
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="your.email@company.com"
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Solution Requirements *
                    </label>
                    <textarea
                      value={formData.requirements}
                      onChange={(e) => setFormData({...formData, requirements: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      rows={4}
                      placeholder="Tell us about your specific needs: What kind of solution are you looking for? What technology stack? What business problem should it solve?"
                      required
                      disabled={isSubmitting}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Be specific about technology requirements, business use case, and expected outcomes
                    </p>
                  </div>

                  {/* Simple Captcha */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Shield className="inline h-4 w-4 mr-1" />
                      Security Check *
                    </label>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-mono bg-gray-100 px-3 py-2 rounded border">
                        {captchaQuestion.question}
                      </span>
                      <input
                        type="number"
                        value={captchaValue}
                        onChange={(e) => setCaptchaValue(e.target.value)}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        placeholder="?"
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 py-3 px-4 rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        <span>Submit Recommendation</span>
                      </>
                    )}
                  </button>
                </form>

                {/* Footer Note */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 text-center">
                    🔒 Your information is secure and will only be used to develop your custom AI agent
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-25 z-30"
          onClick={handleToggle}
        />
      )}
    </>
  );
};

export default RecommendationWidget;